import {isFeatureEnabled} from '@github-ui/feature-flags'
import {IssueFieldSingleSelectValueToken} from '@github-ui/issue-metadata/IssueFieldSingleSelectValueToken'
import {IssueTypeToken} from '@github-ui/issue-type-token'
import {useAnalytics} from '@github-ui/use-analytics'
import {CheckIcon, ChevronRightIcon, InfoIcon, KebabHorizontalIcon, XIcon} from '@primer/octicons-react'
import {
  ActionList,
  ActionMenu,
  AnchoredOverlay,
  Button,
  IconButton,
  IssueLabelToken,
  Label,
  Link,
  RelativeTime,
} from '@primer/react'
import {clsx} from 'clsx'
import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {
  type ApplySuggestionRef,
  commitApplyPendingIssueSuggestionsMutation,
} from '../../mutations/apply-pending-issue-suggestions-mutation'
import {
  commitRejectPendingIssueSuggestionsMutation,
  type RejectSuggestionRef,
} from '../../mutations/reject-pending-issue-suggestions-mutation'
import type {useRecentAgentActivity_issue$key} from './__generated__/useRecentAgentActivity_issue.graphql'
import styles from './RecentAgentActivity.module.css'
import {useDismissRecentAgentActivity} from './use-dismiss-recent-agent-activity'
import type {AgentAction, LabelInfo, PendingSuggestion} from './use-recent-agent-activity'
import {computeRecentActivitySince, useRecentAgentActivity} from './use-recent-agent-activity'

type ActionRationaleProps = {
  rationale: string
  fieldName: string
}

function ActionRationale({rationale, fieldName}: ActionRationaleProps) {
  const [open, setOpen] = useState(false)
  const {sendAnalyticsEvent} = useAnalytics()

  const handleOpen = useCallback(() => {
    setOpen(true)
    sendAnalyticsEvent('recent_agent_actions_panel.rationale_clicked', 'RECENT_AGENT_ACTIVITY', {
      rationaleLength: rationale.length,
    })
  }, [sendAnalyticsEvent, rationale.length])

  const handleClose = useCallback(() => setOpen(false), [])

  return (
    <AnchoredOverlay
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      renderAnchor={props => (
        // eslint-disable-next-line primer-react/a11y-remove-disable-tooltip
        <IconButton
          {...props}
          icon={InfoIcon}
          variant="invisible"
          size="small"
          aria-label={`View rationale for ${fieldName}`}
          unsafeDisableTooltip
          className={styles.rationaleIcon}
        />
      )}
    >
      <div className={styles.rationaleOverlay}>{rationale}</div>
    </AnchoredOverlay>
  )
}

type ActionRowProps = {
  action: AgentAction
  repoNameWithOwner: string
}

const LabelTokenWithRationale = memo(function LabelTokenWithRationale({
  label,
  repoNameWithOwner,
}: {
  label: LabelInfo
  repoNameWithOwner: string
}) {
  return (
    <>
      <Link href={`/${repoNameWithOwner}/issues?q=${encodeURIComponent(`label:"${label.name}"`)}`}>
        <IssueLabelToken
          text={label.name}
          fillColor={label.color ? `#${label.color}` : undefined}
          className={styles.clickableToken}
        />
      </Link>
      {label.rationale && <ActionRationale rationale={label.rationale} fieldName={label.name} />}
    </>
  )
})

function ActionValue({action, repoNameWithOwner}: ActionRowProps) {
  if (action.labels && action.labels.length > 0) {
    return (
      <span className={styles.labelTokens}>
        {action.labels.map(label => (
          <LabelTokenWithRationale key={label.name} label={label} repoNameWithOwner={repoNameWithOwner} />
        ))}
      </span>
    )
  }

  if (action.type === 'type_set' && action.color && action.value) {
    return (
      <span className={styles.labelTokens}>
        <IssueTypeToken
          name={action.value}
          color={action.color}
          href={`/${repoNameWithOwner}/issues?q=${encodeURIComponent(`type:"${action.value}"`)}`}
          getTooltipText={() => undefined}
        />
        {action.rationale && <ActionRationale rationale={action.rationale} fieldName={action.fieldName} />}
      </span>
    )
  }

  if (action.type === 'field_set' && action.value) {
    const fieldSearchLink = `/${repoNameWithOwner}/issues?q=${encodeURIComponent(`field.${action.fieldName.replaceAll(' ', '-')}:"${action.value}"`)}`
    if (action.color) {
      return (
        <span className={styles.labelTokens}>
          <Link href={fieldSearchLink}>
            <IssueFieldSingleSelectValueToken
              name={action.value}
              color={action.color}
              getTooltipText={() => undefined}
              tokenClassName={styles.clickableToken}
            />
          </Link>
          {action.rationale && <ActionRationale rationale={action.rationale} fieldName={action.fieldName} />}
        </span>
      )
    }
    return (
      <span className={styles.labelTokens}>
        <Link href={fieldSearchLink} inline>
          {action.value}
        </Link>
        {action.rationale && <ActionRationale rationale={action.rationale} fieldName={action.fieldName} />}
      </span>
    )
  }

  return <span className={styles.fieldValue}>{action.value ?? ''}</span>
}

const ActionRow = memo(function ActionRow({action, repoNameWithOwner}: ActionRowProps) {
  return (
    <div className={styles.actionRow} role="listitem">
      <CheckIcon size={16} className={styles.checkIcon} aria-hidden="true" />
      <span className={styles.fieldName}>{action.fieldName}</span>
      <span className={styles.arrow} aria-hidden="true">
        →
      </span>
      <ActionValue action={action} repoNameWithOwner={repoNameWithOwner} />
      <span className={styles.timestamp}>
        <RelativeTime date={new Date(action.createdAt)} />
      </span>
    </div>
  )
})

type PendingSuggestionRowProps = {
  suggestion: PendingSuggestion
  onApprove: (suggestion: PendingSuggestion) => void
  onReject: (suggestion: PendingSuggestion) => void
  repoNameWithOwner: string
}

const PendingSuggestionRow = memo(function PendingSuggestionRow({
  suggestion,
  onApprove,
  onReject,
  repoNameWithOwner,
}: PendingSuggestionRowProps) {
  const handleApprove = useCallback(() => onApprove(suggestion), [onApprove, suggestion])
  const handleReject = useCallback(() => onReject(suggestion), [onReject, suggestion])
  return (
    <div className={styles.pendingRow} role="listitem">
      <span className={styles.fieldName}>{suggestion.displayFieldName}</span>
      <span className={styles.arrow} aria-hidden="true">
        →
      </span>
      <span className={styles.labelTokens}>
        {suggestion.type === 'type_set' && suggestion.color && suggestion.value ? (
          <IssueTypeToken
            name={suggestion.value}
            color={suggestion.color}
            href={`/${repoNameWithOwner}/issues?q=${encodeURIComponent(`type:"${suggestion.value}"`)}`}
            getTooltipText={() => undefined}
          />
        ) : suggestion.type === 'label_added' && suggestion.value ? (
          <Link href={`/${repoNameWithOwner}/issues?q=${encodeURIComponent(`label:"${suggestion.value}"`)}`}>
            <IssueLabelToken
              text={suggestion.value}
              fillColor={suggestion.color ? `#${suggestion.color}` : undefined}
              className={styles.clickableToken}
            />
          </Link>
        ) : suggestion.type === 'field_set' && suggestion.value ? (
          suggestion.color ? (
            <Link
              href={`/${repoNameWithOwner}/issues?q=${encodeURIComponent(`field.${suggestion.displayFieldName.replaceAll(' ', '-')}:"${suggestion.value}"`)}`}
            >
              <IssueFieldSingleSelectValueToken
                name={suggestion.value}
                color={suggestion.color}
                getTooltipText={() => undefined}
                tokenClassName={styles.clickableToken}
              />
            </Link>
          ) : (
            <Link
              href={`/${repoNameWithOwner}/issues?q=${encodeURIComponent(`field.${suggestion.displayFieldName.replaceAll(' ', '-')}:"${suggestion.value}"`)}`}
              inline
            >
              {suggestion.value}
            </Link>
          )
        ) : (
          <span className={styles.fieldValue}>{suggestion.value ?? ''}</span>
        )}
        {suggestion.rationale && (
          <ActionRationale rationale={suggestion.rationale} fieldName={suggestion.displayFieldName} />
        )}
      </span>
      <span className={styles.pendingActions}>
        <IconButton
          icon={CheckIcon}
          aria-label={`Approve ${suggestion.displayFieldName} suggestion${suggestion.value ? `: ${suggestion.value}` : ''}`}
          size="small"
          variant="invisible"
          onClick={handleApprove}
        />
        <IconButton
          icon={XIcon}
          aria-label={`Reject ${suggestion.displayFieldName} suggestion${suggestion.value ? `: ${suggestion.value}` : ''}`}
          size="small"
          variant="invisible"
          onClick={handleReject}
        />
      </span>
    </div>
  )
})

type RecentAgentActivityProps = {
  issue: useRecentAgentActivity_issue$key
}

const FEEDBACK_URL = 'https://github-grid.enterprise.slack.com/archives/C0ASY8L0CQ3'

function buildApplyRef(suggestion: PendingSuggestion): ApplySuggestionRef | null {
  const baseOptimisticEvent = {
    actorTypename: suggestion.actorTypename,
    actorLogin: suggestion.actorLogin,
    actorAvatarUrl: suggestion.actorAvatarUrl,
    actorDisplayName: suggestion.actorDisplayName,
    rationale: suggestion.rationale,
  }
  if (suggestion.type === 'type_set' && suggestion.issueTypeId && suggestion.issueTypeName) {
    return {
      kind: 'TYPE',
      issueTypeId: suggestion.issueTypeId,
      optimisticEvent: {
        ...baseOptimisticEvent,
        issueTypeName: suggestion.issueTypeName,
        issueTypeColor: suggestion.issueTypeColor,
      },
    }
  }
  if (suggestion.type === 'label_added' && suggestion.labelId && suggestion.value) {
    return {
      kind: 'LABEL',
      labelId: suggestion.labelId,
      optimisticEvent: {
        ...baseOptimisticEvent,
        labelName: suggestion.value,
        labelColor: suggestion.color,
      },
    }
  }
  if (suggestion.type === 'field_set' && suggestion.issueFieldId) {
    return {
      kind: 'FIELD',
      issueFieldId: suggestion.issueFieldId,
      optimisticEvent: {
        ...baseOptimisticEvent,
        fieldName: suggestion.displayFieldName,
        value: suggestion.value,
        color: suggestion.color,
      },
    }
  }
  return null
}

function buildRejectRef(suggestion: PendingSuggestion): RejectSuggestionRef | null {
  if (suggestion.type === 'type_set' && suggestion.issueTypeId) {
    return {kind: 'TYPE', issueTypeId: suggestion.issueTypeId}
  }
  if (suggestion.type === 'label_added' && suggestion.labelId) {
    return {kind: 'LABEL', labelId: suggestion.labelId}
  }
  if (suggestion.type === 'field_set' && suggestion.issueFieldId) {
    return {kind: 'FIELD', issueFieldId: suggestion.issueFieldId}
  }
  return null
}

export function RecentAgentActivity({issue}: RecentAgentActivityProps) {
  const {
    hasRecentActivity,
    groups,
    totalActionCount,
    viewerCanUpdateMetadata,
    issueId,
    repoNameWithOwner,
    pendingSuggestions,
  } = useRecentAgentActivity(issue)
  const {isDismissed, dismissForIssue, dismissForever} = useDismissRecentAgentActivity(issueId)
  const [expanded, setExpanded] = useState(false)
  const [recentActivitySince] = useState(computeRecentActivitySince)
  const {sendAnalyticsEvent} = useAnalytics()
  const environment = useRelayEnvironment()

  const pendingSuggestionRows = useMemo(
    () => pendingSuggestions.groups.flatMap(group => group.suggestions.filter(s => s.state === 'PENDING')),
    [pendingSuggestions.groups],
  )
  const pendingCount = pendingSuggestionRows.length

  const handleApprove = useCallback(
    (suggestion: PendingSuggestion) => {
      sendAnalyticsEvent('issues.recent_agent_activity.suggestion_approved', 'RECENT_AGENT_ACTIVITY', {
        suggestion_type: suggestion.type,
      })
      const ref = buildApplyRef(suggestion)
      if (!ref) return
      commitApplyPendingIssueSuggestionsMutation({
        environment,
        issueId,
        actorId: suggestion.actorId,
        suggestions: [ref],
        since: recentActivitySince,
      })
    },
    [environment, issueId, recentActivitySince, sendAnalyticsEvent],
  )

  const handleReject = useCallback(
    (suggestion: PendingSuggestion) => {
      sendAnalyticsEvent('issues.recent_agent_activity.suggestion_rejected', 'RECENT_AGENT_ACTIVITY', {
        suggestion_type: suggestion.type,
      })
      const ref = buildRejectRef(suggestion)
      if (!ref) return
      commitRejectPendingIssueSuggestionsMutation({
        environment,
        issueId,
        actorId: suggestion.actorId,
        suggestions: [ref],
      })
    },
    [environment, issueId, sendAnalyticsEvent],
  )

  const handleApproveAll = useCallback(() => {
    // The unified mutation requires a single actorId per call; batch suggestions per actor.
    const byActor = new Map<string, {suggestions: PendingSuggestion[]; refs: ApplySuggestionRef[]}>()
    for (const suggestion of pendingSuggestionRows) {
      const ref = buildApplyRef(suggestion)
      if (!ref) continue
      let bucket = byActor.get(suggestion.actorId)
      if (!bucket) {
        bucket = {suggestions: [], refs: []}
        byActor.set(suggestion.actorId, bucket)
      }
      bucket.suggestions.push(suggestion)
      bucket.refs.push(ref)
    }
    for (const [actorId, {suggestions, refs}] of byActor) {
      for (const suggestion of suggestions) {
        sendAnalyticsEvent('issues.recent_agent_activity.suggestion_approved', 'RECENT_AGENT_ACTIVITY', {
          suggestion_type: suggestion.type,
        })
      }
      commitApplyPendingIssueSuggestionsMutation({
        environment,
        issueId,
        actorId,
        suggestions: refs,
        since: recentActivitySince,
      })
    }
  }, [pendingSuggestionRows, environment, issueId, recentActivitySince, sendAnalyticsEvent])

  const handleRejectAll = useCallback(() => {
    // The unified mutation requires a single actorId per call; batch suggestions per actor.
    const byActor = new Map<string, {suggestions: PendingSuggestion[]; refs: RejectSuggestionRef[]}>()
    for (const suggestion of pendingSuggestionRows) {
      const ref = buildRejectRef(suggestion)
      if (!ref) continue
      let bucket = byActor.get(suggestion.actorId)
      if (!bucket) {
        bucket = {suggestions: [], refs: []}
        byActor.set(suggestion.actorId, bucket)
      }
      bucket.suggestions.push(suggestion)
      bucket.refs.push(ref)
    }
    for (const [actorId, {suggestions, refs}] of byActor) {
      for (const suggestion of suggestions) {
        sendAnalyticsEvent('issues.recent_agent_activity.suggestion_rejected', 'RECENT_AGENT_ACTIVITY', {
          suggestion_type: suggestion.type,
        })
      }
      commitRejectPendingIssueSuggestionsMutation({
        environment,
        issueId,
        actorId,
        suggestions: refs,
      })
    }
  }, [pendingSuggestionRows, environment, issueId, sendAnalyticsEvent])

  const handleToggle = useCallback(() => {
    const next = !expanded
    setExpanded(next)
    sendAnalyticsEvent(
      next ? 'recent_agent_actions_panel.expanded' : 'recent_agent_actions_panel.minimized',
      'RECENT_AGENT_ACTIVITY',
      {
        action_count: totalActionCount,
        agent_count: groups.length,
      },
    )
  }, [expanded, sendAnalyticsEvent, totalActionCount, groups.length])

  const hasFiredDisplayedRef = useRef(false)
  useEffect(() => {
    if (hasRecentActivity && viewerCanUpdateMetadata && !isDismissed && !hasFiredDisplayedRef.current) {
      hasFiredDisplayedRef.current = true
      sendAnalyticsEvent('recent_agent_actions_panel.displayed', 'RECENT_AGENT_ACTIVITY', {
        action_count: totalActionCount,
        agent_count: groups.length,
      })
    }
  }, [hasRecentActivity, viewerCanUpdateMetadata, isDismissed, sendAnalyticsEvent, totalActionCount, groups.length])

  if ((!hasRecentActivity && pendingCount === 0) || !viewerCanUpdateMetadata || isDismissed) {
    return null
  }

  const primaryGroup = groups[0] ?? {
    agentName: pendingSuggestions.groups[0]?.agentName ?? '',
    agentSlug: pendingSuggestions.groups[0]?.agentSlug ?? '',
    actorLogin: pendingSuggestions.groups[0]?.actorLogin ?? '',
    actorAvatarUrl: pendingSuggestions.groups[0]?.actorAvatarUrl ?? '',
    actions: [],
  }
  if (!primaryGroup.agentSlug) return null

  return (
    <div
      className={clsx(styles.container, isFeatureEnabled('issue_inline_avatars') && styles.container_inlineAvatars)}
      data-testid="recent-agent-activity"
    >
      <button
        type="button"
        className={styles.trigger}
        onClick={handleToggle}
        aria-expanded={expanded}
        aria-controls="recent-agent-activity-content"
      >
        <span className={styles.label}>Recent agent activity</span>
        <span className={styles.actorInfo}>
          <Label variant="secondary">AI</Label>
          <span className={styles.count}>
            {pendingCount > 0 && <span className={styles.pendingCount}>{pendingCount} pending</span>}
            {pendingCount > 0 && totalActionCount > 0 && <span> · </span>}
            {totalActionCount > 0 && <span>{`${totalActionCount} applied`}</span>}
          </span>
        </span>
        <ChevronRightIcon
          size={16}
          className={clsx(styles.chevron, expanded && styles.chevronExpanded)}
          aria-hidden="true"
        />
      </button>

      {expanded && (
        <div id="recent-agent-activity-content" className={styles.expandedContent}>
          <div className={styles.content} role="list" aria-label="Recent agent activity">
            {pendingSuggestionRows.map(suggestion => {
              const subjectKey =
                suggestion.issueTypeId ??
                suggestion.labelId ??
                suggestion.issueFieldId ??
                `${suggestion.type}:${suggestion.displayFieldName}`
              return (
                <PendingSuggestionRow
                  key={`pending-${suggestion.actorId}:${subjectKey}`}
                  suggestion={suggestion}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  repoNameWithOwner={repoNameWithOwner}
                />
              )
            })}
            {groups.map(group =>
              group.actions.map(action => (
                <ActionRow
                  key={`${group.agentSlug}-${action.fieldName}-${action.value}-${action.createdAt}`}
                  action={action}
                  repoNameWithOwner={repoNameWithOwner}
                />
              )),
            )}
          </div>
          <div className={styles.footer}>
            <span className={styles.footerLinks}>
              {pendingSuggestionRows.length > 1 && (
                <>
                  <Button variant="primary" size="small" onClick={handleApproveAll}>
                    Approve all
                  </Button>
                  <Button variant="default" size="small" onClick={handleRejectAll}>
                    Reject all
                  </Button>
                </>
              )}
            </span>
            <span className={styles.footerActions}>
              <Label variant="success">Private preview</Label>
              <Link href={FEEDBACK_URL} target="_blank" rel="noopener noreferrer" inline>
                Give feedback
              </Link>
              <ActionMenu>
                <ActionMenu.Anchor>
                  <IconButton icon={KebabHorizontalIcon} aria-label="More actions" size="small" variant="invisible" />
                </ActionMenu.Anchor>
                <ActionMenu.Overlay>
                  <ActionList>
                    <ActionList.Item onSelect={dismissForIssue}>Dismiss</ActionList.Item>
                    <ActionList.Item onSelect={dismissForever}>Never show this again</ActionList.Item>
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
